import React from 'react';
import { ParameterOperators } from 'src/components/atoms';
import { Attributes } from 'src/components/organisms';
import { FormState } from 'src/types';
import { DefaultOption, ParameterOptions } from '../DropdownSelect';
import { ParameterDropdown } from '../ParameterDropdown';
import { ParameterType } from './helpers';
import { ParameterIconButton, ParameterIconList } from './Parameter.styles';
import { useParameter } from './useParameter';

export interface ParameterList {
  site?: string[];
  department?: string[];
  country?: string[];
  team?: string[];
  anonymous?: boolean[];
  relationshipToCompany?: string[];
  systemRoles?: string[];
  submissionMode?: number[];
  reportedCategory?: string[];
  'userMetaData.organization'?: string[];
  'userMetaData.division'?: string[];
  'Call.organisationHotlineNumber'?: string[];
  coiCategory?: string[];
  questionnaireId?: string[];
  ''?: string[];
}

export type ParameterBaseProps = {
  parameter: ParameterType;
  isFirstParameter?: boolean;
  isLastParameter?: boolean;
  parameterOptions: DefaultOption[];
  parameterList: ParameterList;
  setParameterList: (value: React.SetStateAction<ParameterList>) => void;
  showOperators?: boolean; // include to see IF/AND
  isInDraggable?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  tabIndex?: number;
};

export type ParameterProps = ParameterBaseProps & {
  options: DefaultOption[];
  attributes: Attributes;
};

const getDropdownState = (disabled: boolean, showParameterWarning: boolean) => {
  if (disabled) {
    return FormState.DISABLED;
  }

  if (showParameterWarning) {
    return FormState.WARNING;
  }

  return FormState.DEFAULT;
};

export const Parameter = ({
  parameter,
  isFirstParameter,
  isLastParameter,
  parameterOptions,
  parameterList,
  setParameterList,
  attributes,
  showOperators,
  options,
  isInDraggable = false,
  isSearchable = false,
  disabled = false,
  tabIndex,
  ...props
}: ParameterProps) => {
  const {
    parameterValue,
    parameterOptionsValue,
    attributesOptions,
    noOptionsSelected,
    emptyParameterExists,
    parameterWithNoValues,
    isNotEmptyParameter,
    showParameterWarning,
    warningText,
  } = useParameter(parameter, parameterList, parameterOptions, attributes);

  const firstColumn = showOperators ? 2 : 1;
  const secondColumn = showOperators ? 3 : 2;
  const lastColumn = showOperators ? 4 : 3;

  const dropdownState = getDropdownState(disabled, showParameterWarning);

  const firstParameterPlaceholder = () => {
    if (isSearchable) {
      return `Search: Parameters`;
    }
    return 'Example: Department';
  };

  const secondParameterPlaceholder = () => {
    if (isSearchable) {
      return `Search${
        parameterValue?.pluralLabel ? `: ${parameterValue?.pluralLabel}` : ''
      }`;
    }
    return 'Example: Technology';
  };

  return (
    <>
      <ParameterOperators
        firstColumn={firstColumn}
        secondColumn={secondColumn}
        isFirstParameter={isFirstParameter}
        isLastParameter={isLastParameter}
        showOperators={showOperators}
      />
      <ParameterDropdown
        column={firstColumn}
        placeholder={firstParameterPlaceholder()}
        options={options}
        onChange={(option) =>
          setParameterList((prev) => {
            const prevParameterList = { ...prev };
            delete prevParameterList[parameter];
            return {
              ...prevParameterList,
              [(option as ParameterOptions).value.toString()]: [],
            };
          })
        }
        isSearchable={isSearchable}
        value={parameterValue}
        manualMenuOpen={isInDraggable}
        state={dropdownState}
        disabled={disabled}
        container-data-testid="parameter-option-dropdown"
        data-testid={`parameter-select-${props['data-testid']}`}
        tabIndex={tabIndex}
        showParameterWarning={showParameterWarning}
        warningText={warningText}
      />
      <ParameterDropdown
        column={secondColumn}
        placeholder={secondParameterPlaceholder()}
        options={attributesOptions}
        onChange={(selectedOptions) =>
          setParameterList((prev) => ({
            ...prev,
            [parameter]: (selectedOptions as ParameterOptions[]).map(
              (option) => option.value,
            ),
          }))
        }
        value={
          (isNotEmptyParameter
            ? parameterOptionsValue
            : null) as DefaultOption | null
        }
        isMulti
        isSearchable={isSearchable}
        manualMenuOpen={isInDraggable}
        state={disabled ? FormState.DISABLED : FormState.DEFAULT}
        disabled={disabled}
        container-data-testid="parameter-value-dropdown"
        data-testid={`options-select-${props['data-testid']}`}
        tabIndex={tabIndex}
      />
      <ParameterIconList column={lastColumn}>
        <ParameterIconButton
          size="medium"
          variant="tertiary"
          iconName="uil-trash-alt"
          onClick={() =>
            setParameterList((prev) => {
              const prevParameterList = { ...prev };
              delete prevParameterList[parameter];
              return { ...prevParameterList };
            })
          }
          disabled={disabled || noOptionsSelected}
          tabIndex={tabIndex}
        />
        <ParameterIconButton
          size="medium"
          variant="tertiary"
          iconName="uil-plus"
          onClick={() => setParameterList((prev) => ({ ...prev, '': [] }))}
          disabled={disabled || emptyParameterExists || parameterWithNoValues}
          data-testid={`add-parameter-${props['data-testid']}`}
          tabIndex={tabIndex}
        />
      </ParameterIconList>
    </>
  );
};
